import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { getJobTitles, deleteJobTitle } from '../../../../redux/Slices/JobTitlesSlice';
import { FaSearch, FaPlus, FaBriefcase, FaFileAlt, FaCalendarAlt, FaEdit, FaTrash, FaEye, FaPrint, FaFileExcel } from 'react-icons/fa';
import SortOrderSelect from '../../../../shared/components/SortOrderSelect';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import PdfPreviewModal from '../../Accounting/Reports/components/PdfPreviewModal';
import { chunkArray, getPdfRowStyles } from '../../../../shared/utils.js/utils';

const JobTitles = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { jobTitles, isLoading, error, success } = useSelector((state) => state.auth);
  
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [jobTitleToDelete, setJobTitleToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const [rawJobTitles, setRawJobTitles] = useState([]);
  const [allJobTitlesForPdf, setAllJobTitlesForPdf] = useState([]);


  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const lastErrorRef = useRef({ message: null, time: 0 });
  const [jobTitleForPrint, setJobTitleForPrint] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' or 'all'
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [pdfChunks, setPdfChunks] = useState([]);
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;
  const searchTimeoutRef = useRef(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortField, setSortField] = useState('name');
  const [isSinglePreviewOpen, setIsSinglePreviewOpen] = useState(false);
  const [singleJobTitleForPreview, setSingleJobTitleForPreview] = useState(null);

  useEffect(() => {
    setTitle('sidenav.hr.jobTitles');
  }, [setTitle]);

  // Fetch all job titles once (no server-side search)
  const fetchJobTitles = async () => {
    try {
      const result = await dispatch(getJobTitles({ language: i18n.language, page: 1, per_page: 1000 }));
      if (getJobTitles.fulfilled.match(result)) {
        const response = result.payload;
        const data = response?.data || response;
        let jobTitlesList = [];
        if (Array.isArray(data?.data)) {
          jobTitlesList = data.data;
        } else if (Array.isArray(data)) {
          jobTitlesList = data;
        }
        setRawJobTitles(jobTitlesList);
      }
    } catch (err) {
      console.error('Error fetching job titles:', err);
    }
  };

  useEffect(() => {
    fetchJobTitles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, i18n.language]);

  // Reset page on search/sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortOrder, sortField]);

  useEffect(() => {
    dispatch(clearSuccess());
    dispatch(clearError());
  }, [dispatch]);

  const handleApplyPerPage = () => {
    const newPerPage = Math.max(1, Math.min(perPageInput, filteredData.length || 1000));
    setPerPage(newPerPage);
    setPerPageInput(newPerPage);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, dispatch, isRTL]);

  // Client-side filtering + sorting
  const filteredData = React.useMemo(() => {
    const q = searchTerm.trim().replace(/\s+/g, ' ').toLowerCase();
    const filtered = !q ? rawJobTitles : rawJobTitles.filter((jt) => (
      (jt.name_ar || '').toLowerCase().includes(q) ||
      (jt.name_en || '').toLowerCase().includes(q) ||
      (jt.name || '').toLowerCase().includes(q) ||
      (jt.description || '').toLowerCase().includes(q)
    ));
    return [...filtered].sort((a, b) => {
      if (sortField === 'created_at') {
        const aVal = new Date(a.created_at || 0).getTime();
        const bVal = new Date(b.created_at || 0).getTime();
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const aVal = (a.name || '').toLowerCase();
      const bVal = (b.name || '').toLowerCase();
      if (sortOrder === 'asc') return aVal.localeCompare(bVal, isRTL ? 'ar' : 'en');
      return bVal.localeCompare(aVal, isRTL ? 'ar' : 'en');
    });
  }, [rawJobTitles, searchTerm, sortOrder, sortField, isRTL]);
  const totalPages = Math.max(1, Math.ceil(filteredData.length / perPage));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedData = filteredData.slice((currentPageSafe - 1) * perPage, currentPageSafe * perPage);

  const formatDate = (dateString) => {
    if (!dateString) return t('jobTitles.dateFormat.notSpecified');
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const colStyles = {
    serial: { width: '5%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px' },
    name: { width: '32%', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px', wordBreak: 'break-word' },
    description: { width: '43%', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px', wordBreak: 'break-word' },
    created: { width: '20%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px' }
  };

  const renderPdfPage = (chunk, chunkIdx, totalPages, elementId = null) => {
    const hasActiveFilters = searchTerm.trim();
    const previewFormatDate = (ds) => ds ? new Date(ds).toLocaleDateString('en-US') : '--';

    return (
      <div
        id={elementId}
        style={{
          width: '794px',
          height: '1123px',
          backgroundColor: '#ffffff',
          color: '#000000',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          padding: '40px',
          boxSizing: 'border-box',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <div style={{ border: '5px solid #000', padding: '3px', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', height: '1043px', overflow: 'hidden' }}>
          <div style={{
            border: '2px solid #000',
            height: '1033px',
            padding: '18px 18px 10px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: 'bold',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            textRendering: 'optimizeLegibility',
            direction: isRTL ? 'rtl' : 'ltr',
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
            width: '100%',
            overflow: 'hidden'
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: `url(${headerLogo})`, backgroundSize: '60%', backgroundPosition: 'center top', backgroundRepeat: 'no-repeat', opacity: 0.05, zIndex: 0, pointerEvents: 'none' }} />
            <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <img src={`${publicUrl}/HeaderAce.png`} alt="Header" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px', display: 'block', flexShrink: 0 }} crossOrigin="anonymous" />
              
              <div style={{ textAlign: 'center', padding: '6px 0 8px', borderBottom: '1px solid #ddd', marginBottom: '4px', direction: isRTL ? 'rtl' : 'ltr', flexShrink: 0 }}>
                <div style={{ fontSize: '16px', fontWeight: '900', color: '#000080' }}>{isRTL ? 'تقرير المسميات الوظيفية' : 'Job Titles Report'}</div>
                <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>{isRTL ? 'تاريخ التقرير' : 'Report Date'}: {new Date().toLocaleDateString('en-US')} — {isRTL ? 'صفحة' : 'Page'} {chunkIdx + 1} / {totalPages}</div>
                
                {hasActiveFilters && (
                  <div style={{ 
                    fontSize: '9px', 
                    color: '#444', 
                    marginTop: '6px', 
                    display: 'flex', 
                    gap: '12px', 
                    justifyContent: 'center', 
                    flexWrap: 'wrap',
                    backgroundColor: 'rgba(238, 242, 247, 0.5)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}>
                    {searchTerm.trim() && <span>{t('common.search', { defaultValue: 'بحث' })}: {searchTerm.trim().replace(/\s+/g, ' ')}</span>}
                  </div>
                )}
              </div>

              <div style={{ marginTop: '12px', direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif', flex: 1 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', backgroundColor: 'rgba(255,255,255,0.7)', tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '5%' }} /><col style={{ width: '32%' }} /><col style={{ width: '43%' }} /><col style={{ width: '20%' }} />
                  </colgroup>
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(238,242,247,0.7)' }}>
                      <th style={colStyles.serial}>#</th>
                      <th style={colStyles.name}>{t('jobTitles.table.name')}</th>
                      <th style={colStyles.description}>{t('jobTitles.table.description')}</th>
                      <th style={colStyles.created}>{t('jobTitles.table.createdAt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chunk.map((job, idx) => {
                      const foundIdx = allJobTitlesForPdf.findIndex(jt => String(jt?.id) === String(job?.id));
                      const serial = foundIdx >= 0 ? foundIdx + 1 : chunkIdx * 25 + idx + 1;
                      const cellBgColor = idx % 2 === 0 ? 'rgba(255,255,255,0.7)' : 'rgba(245,245,245,0.5)';
                      return (
                        <tr key={job.id} style={{ backgroundColor: cellBgColor }}>
                          <td style={{ ...colStyles.serial, backgroundColor: cellBgColor }}>{serial}</td>
                          <td style={{ ...colStyles.name, backgroundColor: cellBgColor }}>{job.name || '—'}</td>
                          <td style={{ ...colStyles.description, backgroundColor: cellBgColor }}>{job.description || '—'}</td>
                          <td style={{ ...colStyles.created, backgroundColor: cellBgColor }}>{previewFormatDate(job.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px', flexShrink: 0 }}>
                <img src={`${publicUrl}/FooterAce.png`} alt="Footer" style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block', borderTop: '4px double #000', paddingTop: '8px' }} crossOrigin="anonymous" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleEdit = (id) => {
    navigate(`/job-titles/edit/${id}`);
  };

  const handleView = (id) => {
    navigate(`/job-titles/details/${id}`);
  };

  const handleDelete = (jobTitle) => {
    setJobTitleToDelete(jobTitle);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (jobTitleToDelete) {
      const idToDelete = jobTitleToDelete.id;
      // Optimistically remove from local state first
      setRawJobTitles(prev => prev.filter(jobTitle => jobTitle.id !== idToDelete));
      setShowDeleteModal(false);
      const jobTitleToDeleteBackup = jobTitleToDelete;
      setJobTitleToDelete(null);

      // Then call the API
      try {
        const result = await dispatch(deleteJobTitle({ id: idToDelete, language: i18n.language }));
        if (deleteJobTitle.fulfilled.match(result)) {
          fetchJobTitles();
        } else if (deleteJobTitle.rejected.match(result)) {
          // Revert on error - restore the deleted job title
          setRawJobTitles(prev => {
            const restored = [...prev, jobTitleToDeleteBackup];
            return restored.sort((a, b) => {
              if (a.id && b.id) return a.id - b.id;
              return 0;
            });
          });
        }
      } catch (error) {
        // Revert on error
        setRawJobTitles(prev => {
          const restored = [...prev, jobTitleToDeleteBackup];
          return restored.sort((a, b) => {
            if (a.id && b.id) return a.id - b.id;
            return 0;
          });
        });
      }
    }
  };

  // Handle print single job title
  const handlePrintJobTitle = async (jobTitle) => {
    try {
      setJobTitleForPrint(jobTitle);
      setPdfType('single');
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise((resolve) => setTimeout(resolve, 300));

      setIsGeneratingPdf(true);

      const element = document.getElementById(`job-title-pdf-${jobTitle.id}`);
      if (!element) {
        toast.error(t('jobTitles.print.error'), { rtl: isRTL });
        return;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, devicePixelRatio * 2);

      const canvas = await html2canvas(element, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        letterRendering: true,
        allowTaint: false,
        removeContainer: false,
        imageTimeout: 15000,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.getElementById(`job-title-pdf-${jobTitle.id}`);
          if (clonedElement) {
            clonedElement.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
            clonedElement.style.fontWeight = 'bold';
            clonedElement.style.direction = isRTL ? 'rtl' : 'ltr';
            clonedElement.style.setProperty('-webkit-font-smoothing', 'antialiased');
            clonedElement.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
            clonedElement.style.textRendering = 'optimizeLegibility';

            const allElements = clonedElement.querySelectorAll('*');
            allElements.forEach(el => {
              if (el.style) {
                el.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
                el.style.fontWeight = 'bold';
                el.style.setProperty('-webkit-font-smoothing', 'antialiased');
                el.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
                el.style.textRendering = 'optimizeLegibility';
              }
            });
          }
        }
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgData = canvas.toDataURL('image/png', 1.0);
      let imgWidth = pdfWidth;
      let imgHeight = (canvas.height * pdfWidth) / canvas.width;

      // Professional solution: If content slightly exceeds one page, scale it down to fit
      const tolerance = 10; // 10mm tolerance
      if (imgHeight > pdfHeight && imgHeight <= pdfHeight + tolerance) {
        const scaleFactor = pdfHeight / imgHeight;
        imgHeight = pdfHeight;
        imgWidth = pdfWidth * scaleFactor;
        const xOffset = (pdfWidth - imgWidth) / 2;
        pdf.addImage(imgData, 'PNG', xOffset, 0, imgWidth, imgHeight, undefined, 'FAST');
      } else if (imgHeight <= pdfHeight) {
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');
      } else {
        let position = 0;
        while (position < imgHeight) {
          if (position > 0) {
            pdf.addPage();
          }
          pdf.addImage(imgData, 'PNG', 0, -position, imgWidth, imgHeight, undefined, 'FAST');
          position += pdfHeight;
        }
      }

      const identifier = jobTitle.name || jobTitle.id;
      pdf.save(`ACE_JobTitle_${identifier}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('Job Title PDF error:', err);
      toast.error(t('jobTitles.print.error'), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setJobTitleForPrint(null);
      setPdfType(null);
    }
  };

  const calculateDynamicChunks = (items) => {
    const tableContainer = document.getElementById('job-titles-measurement-container');
    const thead = tableContainer ? tableContainer.querySelector('thead') : null;
    const theadHeight = thead ? thead.offsetHeight : 35;
    const rows = tableContainer ? tableContainer.querySelectorAll('tbody tr') : [];
    
    const MAX_TABLE_HEIGHT = 740;
    const chunks = [];
    let currentChunk = [];
    let currentHeight = theadHeight;

    items.forEach((item, index) => {
      const rowElement = rows[index];
      let rowHeight = 45; // Default fallback height
      
      if (rowElement && rowElement.offsetHeight > 0) {
        rowHeight = rowElement.offsetHeight;
      } else {
        const nameLen = (item.name || '').length;
        const descLen = (item.description || '').length;
        const maxTextLen = Math.max(nameLen / 30, descLen / 40);
        const lines = Math.max(1, Math.ceil(maxTextLen));
        rowHeight = 35 + (lines * 15);
      }

      if (currentHeight + rowHeight > MAX_TABLE_HEIGHT && currentChunk.length > 0) {
        chunks.push(currentChunk);
        currentChunk = [];
        currentHeight = theadHeight;
      }
      currentChunk.push(item);
      currentHeight += rowHeight;
    });

    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }
    return chunks;
  };

  // Handle preview all job titles
  const handlePreviewAll = async () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error(t('jobTitles.preview.noData'), { rtl: isRTL });
      return;
    }
    setAllJobTitlesForPdf(filteredData);
    await new Promise((resolve) => setTimeout(resolve, 300));
    const chunks = calculateDynamicChunks(filteredData);
    setPdfChunks(chunks);
    setPdfType('all');
    setIsPreviewOpen(true);
  };

  // Handle preview single job title
  const handlePreviewSingle = (jobTitle) => {
    setSingleJobTitleForPreview(jobTitle);
    setIsSinglePreviewOpen(true);
  };

  // Handle print all job titles
  const handlePrintAll = async () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error(t('jobTitles.print.noData'), { rtl: isRTL });
      return;
    }

    try {
      setAllJobTitlesForPdf(filteredData);
      await new Promise((resolve) => setTimeout(resolve, 300));

      const chunks = calculateDynamicChunks(filteredData);
      setPdfChunks(chunks);
      setPdfType('all');

      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => setTimeout(resolve, 100));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      setIsGeneratingPdf(true);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.min(2, Math.max(1.5, devicePixelRatio));

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
        await new Promise((resolve) => setTimeout(resolve, 100));

        const element = document.getElementById(`job-titles-pdf-all-${chunkIndex}`);
        if (!element) continue;

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          letterRendering: false,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 10000,
          onclone: (clonedDoc) => {
            const clonedElement = clonedDoc.getElementById(`job-titles-pdf-all-${chunkIndex}`);
            if (clonedElement) {
              clonedElement.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = 'bold';
              clonedElement.style.direction = isRTL ? 'rtl' : 'ltr';
            }
          }
        });

        const imgData = canvas.toDataURL('image/png', 1.0);
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        if (chunkIndex > 0) {
          pdf.addPage();
        }

        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight, undefined, 'FAST');
      }

      pdf.save(`ACE_JobTitles_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(t('jobTitles.print.success'), { rtl: isRTL });
    } catch (error) {
      console.error('Job Titles PDF error:', error);
      toast.error(t('jobTitles.print.error'), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  // Handle export single job title to Excel
  const handleExportJobTitleToExcel = (jobTitle) => {
    const data = [{
      [t('jobTitles.table.name')]: jobTitle.name || '—',
      [t('jobTitles.table.description')]: jobTitle.description || '—',
      [t('jobTitles.table.createdAt')]: formatDate(jobTitle.created_at) || '—'
    }];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isRTL ? 'مسمى وظيفي' : 'Job Title');
    const identifier = jobTitle.name || jobTitle.id;
    const fileName = `${isRTL ? 'مسمى_وظيفي' : 'JobTitle'}_${identifier}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(t('jobTitles.export.success'), { rtl: isRTL });
  };

  // Handle export all job titles to Excel
  const handleExportAllToExcel = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error(t('jobTitles.export.noData'), { rtl: isRTL });
      return;
    }

    const data = filteredData.map((jobTitle, index) => ({
      [t('common.serialNumber')]: index + 1,
      [t('jobTitles.table.name')]: jobTitle.name || '—',
      [t('jobTitles.table.description')]: jobTitle.description || '—',
      [t('jobTitles.table.createdAt')]: formatDate(jobTitle.created_at) || '—'
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isRTL ? 'المسميات الوظيفية' : 'Job Titles');
    const fileName = `${isRTL ? 'المسميات_الوظيفية' : 'JobTitles'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(t('jobTitles.export.success'), { rtl: isRTL });
  };

  if (isLoading && rawJobTitles.length === 0) {
    return (
      <div style={{
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div style={{
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir
    }}>
      {/* ── Row 1: Title + Add ── */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <h1 style={{ fontSize: '22px', fontWeight: 'bold', margin: 0, color: 'white' }}>
          {t('jobTitles.title')}
        </h1>
        <button
          onClick={() => navigate('/add-job-title')}
          style={{
            backgroundColor: '#AC2000',
            color: 'white',
            border: 'none',
            padding: '10px 18px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            whiteSpace: 'nowrap',
          }}
        >
          <FaPlus />
          {t('jobTitles.addButton')}
        </button>
      </div>

      {/* ── Row 2: Search + Sort + PerPage ── */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
          <FaSearch style={{
            position: 'absolute',
            [isRTL ? 'right' : 'left']: '15px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#666',
            fontSize: '16px'
          }} />
          <input
            type="text"
            placeholder={t('jobTitles.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: isRTL ? '10px 42px 10px 14px' : '10px 14px 10px 42px',
              backgroundColor: '#1b3350',
              border: '1px solid #333',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              direction: dir,
              textAlign,
              boxSizing: 'border-box'
            }}
          />
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 160, color: 'white', fontSize: 13, direction: isRTL ? 'rtl' : 'ltr' }}>
          <span>{t('common.sortBy', { defaultValue: 'ترتيب حسب' })}</span>
          <select
            value={sortField}
            onChange={e => setSortField(e.target.value)}
            style={{ padding: '10px 12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr' }}
          >
            <option value="name">{t('jobTitles.table.name')}</option>
            <option value="created_at">{t('jobTitles.table.createdAt')}</option>
          </select>
        </label>
        <SortOrderSelect value={sortOrder} onChange={val => setSortOrder(val)} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          <label style={{ color: '#aaa', fontSize: '13px', whiteSpace: 'nowrap' }}>
            {t('common.perPage', { defaultValue: 'عدد السجلات' })}:
          </label>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button
              onClick={() => { const newValue = Math.max(1, perPageInput - 1); setPerPageInput(newValue); }}
              style={{ backgroundColor: '#1b3350', color: 'white', border: '1px solid #333', padding: '6px 10px', borderRadius: '6px 0 0 6px', cursor: 'pointer', fontSize: '14px', minWidth: '30px' }}
            >−</button>
            <input
              type="number"
              min="1"
              max={filteredData?.length || 1000}
              value={perPageInput}
              onChange={(e) => { const value = Math.max(1, parseInt(e.target.value) || 1); setPerPageInput(value); }}
              style={{ width: '60px', padding: '6px 8px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderLeft: 'none', borderRight: 'none', borderRadius: 0, color: 'white', fontSize: '14px', textAlign: 'center' }}
            />
            <button
              onClick={() => { const maxValue = filteredData?.length || 1000; const newValue = Math.min(maxValue, perPageInput + 1); setPerPageInput(newValue); }}
              style={{ backgroundColor: '#1b3350', color: 'white', border: '1px solid #333', padding: '6px 10px', borderRadius: '0 6px 6px 0', cursor: 'pointer', fontSize: '14px', minWidth: '30px' }}
            >+</button>
          </div>
          <button
            onClick={handleApplyPerPage}
            style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', whiteSpace: 'nowrap' }}
          >
            {t('common.apply', { defaultValue: 'تطبيق' })}
          </button>
        </div>
      </div>

      {/* ── Row 3: Export / Print ── */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button
          onClick={handlePreviewAll}
          style={{ backgroundColor: '#444', color: 'white', border: 'none', padding: '9px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <FaEye /> {t('jobTitles.preview.all')}
        </button>
        <button
          onClick={handlePrintAll}
          disabled={isGeneratingPdf}
          style={{ backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8', color: 'white', border: 'none', padding: '9px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: isGeneratingPdf ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px', opacity: isGeneratingPdf ? 0.6 : 1 }}
        >
          <FaPrint />
          {isGeneratingPdf ? t('jobTitles.print.generating') : t('jobTitles.print.all')}
        </button>
        <button
          onClick={handleExportAllToExcel}
          style={{ backgroundColor: '#28a745', color: 'white', border: 'none', padding: '9px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <FaFileExcel />
          {t('jobTitles.export.all')}
        </button>
      </div>

      {/* جدول المسميات الوظيفية */}
      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #333'
      }}>


        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            minWidth: '800px'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#AC2000' }}>
                <th style={{
                  padding: '15px',
                  textAlign: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderBottom: '1px solid #333',
                  backgroundColor: '#AC2000'
                }}>
                  {t('jobTitles.table.number')}
                </th>
                <th style={{
                  padding: '15px',
                  textAlign: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderBottom: '1px solid #333',
                  backgroundColor: '#AC2000'
                }}>
                  <FaBriefcase className="mx-2" />
                  {t('jobTitles.table.name')}
                </th>
                <th style={{
                  padding: '15px',
                  textAlign: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderBottom: '1px solid #333',
                  backgroundColor: '#AC2000'
                }}>
                  <FaFileAlt className="mx-2" />
                  {t('jobTitles.table.description')}
                </th>
                <th style={{
                  padding: '15px',
                  textAlign: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderBottom: '1px solid #333',
                  backgroundColor: '#AC2000'
                }}>
                  <FaCalendarAlt className="mx-2" />
                  {t('jobTitles.table.createdAt')}
                </th>
                <th style={{
                  padding: '15px',
                  textAlign: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderBottom: '1px solid #333',
                  backgroundColor: '#AC2000'
                }}>
                  {t('jobTitles.table.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData && paginatedData.length > 0 ? (
                paginatedData.map((job, index) => (
                  <tr key={job.id} style={{
                    borderBottom: '1px solid #333'
                  }}>
                    <td style={{
                      padding: '15px',
                      color: 'white',
                      fontWeight: '500',
                      textAlign: 'center'
                    }}>
                      {sortOrder === 'asc'
                        ? (currentPageSafe - 1) * perPage + index + 1
                        : filteredData.length - ((currentPageSafe - 1) * perPage + index)}
                    </td>
                    <td style={{
                      padding: '15px',
                      color: 'white',
                      fontWeight: '500',
                      textAlign: 'center'
                    }}>
                      {job.name}
                    </td>
                    <td style={{
                      padding: '15px',
                      color: 'white',
                      opacity: 0.9,
                      maxWidth: '300px',
                      wordWrap: 'break-word',
                      textAlign: 'center',
                      direction: dir
                    }}>
                      {job.description}
                    </td>
                    <td style={{
                      padding: '15px',
                      color: 'white',
                      opacity: 0.9,
                      textAlign: 'center'
                    }}>
                      {formatDate(job.created_at)}
                    </td>
                    <td style={{
                      padding: '15px',
                      textAlign: 'center'
                    }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'nowrap' }}>
                        <button
                          onClick={() => handlePreviewSingle(job)}
                          style={{
                            backgroundColor: '#444',
                            color: 'white',
                            border: 'none',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            flexShrink: 0
                          }}
                          title={t('jobTitles.preview.all')}
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => handlePrintJobTitle(job)}
                          disabled={isGeneratingPdf}
                          style={{
                            backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
                            color: 'white',
                            border: 'none',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                            opacity: isGeneratingPdf ? 0.6 : 1,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title={t('jobTitles.print.single')}
                        >
                          <FaPrint />
                        </button>
                        <button
                          onClick={() => handleExportJobTitleToExcel(job)}
                          style={{
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title={t('jobTitles.export.single')}
                        >
                          <FaFileExcel />
                        </button>
                        <button
                          onClick={() => handleEdit(job.id)}
                          style={{
                            backgroundColor: '#B3B3B3',
                            color: 'white',
                            border: 'none',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            flexShrink: 0
                          }}
                          title={t('jobTitles.actions.edit')}
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(job)}
                          style={{
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            flexShrink: 0
                          }}
                          title={t('jobTitles.actions.delete')}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', color: 'white', padding: '20px' }}>
                    {t('jobTitles.noData')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* الترقيم (نمط موحد) */}
        {totalPages > 1 && (() => {
          const pages = [];
          const showPages = 5;
          let start = Math.max(1, currentPageSafe - Math.floor(showPages / 2));
          let end = Math.min(totalPages, start + showPages - 1);
          if (end - start < showPages - 1) {
            start = Math.max(1, end - showPages + 1);
          }
          for (let i = start; i <= end; i++) {
            pages.push(i);
          }
          return (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px', paddingBottom: '20px' }}>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPageSafe - 1))}
                disabled={currentPageSafe === 1}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPageSafe === 1 ? '#333' : '#AC2000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: currentPageSafe === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                {t('jobTitles.pagination.previous')}
              </button>
              {pages.map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: page === currentPageSafe ? '#AC2000' : '#1a1f2e',
                    color: 'white',
                    border: '1px solid #333',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPageSafe + 1))}
                disabled={currentPageSafe === totalPages}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPageSafe === totalPages ? '#333' : '#AC2000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: currentPageSafe === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                {t('jobTitles.pagination.next')}
              </button>
            </div>
          );
        })()}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', direction: dir }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{
              backgroundColor: '#1b3350',
              border: '1px solid #333',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <div className="modal-header" style={{ border: 'none', borderBottom: '1px solid #333' }}>
                <h5 className="modal-title text-white" style={{ textAlign }}>{t('jobTitles.deleteModal.title')}</h5>
              </div>
              <div className="modal-body text-white" style={{ textAlign }}>
                <p>{t('jobTitles.deleteModal.message', { name: jobTitleToDelete?.name })}</p>
                <p style={{ color: 'white' }}>{t('jobTitles.deleteModal.warning')}</p>
              </div>
              <div className="modal-footer" style={{ border: 'none', flexDirection: isRTL ? 'row' : 'row-reverse' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                  style={{
                    backgroundColor: '#6c757d',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 20px'
                  }}
                >
                  {t('jobTitles.deleteModal.cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  style={{
                    backgroundColor: '#dc3545',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 20px'
                  }}
                >
                  {t('jobTitles.deleteModal.confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print Templates */}
      {/* Single Job Title Print Template */}
      {jobTitleForPrint && pdfType === 'single' && (
        <div
          id={`job-title-pdf-${jobTitleForPrint.id}`}
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 0,
            width: '794px',
            height: '1123px',
            backgroundColor: '#ffffff',
            color: '#000000',
            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: 'bold',
            padding: '40px',
            boxSizing: 'border-box',
            overflow: 'visible'
          }}
        >
          <div style={{
            border: '5px solid #000',
            padding: '3px',
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
            width: '100%',
            height: '100%',
            overflow: 'hidden'
          }}>
            <div style={{
              border: '2px solid #000',
              height: '100%',
              padding: '18px 18px 10px',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              textRendering: 'optimizeLegibility',
              direction: isRTL ? 'rtl' : 'ltr',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              width: '100%',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundImage: `url(${headerLogo})`,
                backgroundSize: '95%',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
                opacity: 0.08,
                zIndex: 0,
                pointerEvents: 'none'
              }} />
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <img
                  src={`${publicUrl}/HeaderAce.png`}
                  alt="Header"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    marginBottom: '8px',
                    borderBottom: '4px double #000',
                    paddingBottom: '8px',
                    flexShrink: 0
                  }}
                  crossOrigin="anonymous"
                />

                <div style={{ textAlign: 'center', padding: '6px 0 8px', borderBottom: '1px solid #ddd', marginBottom: '4px', flexShrink: 0, direction: isRTL ? 'rtl' : 'ltr' }}>
                  <div style={{ fontSize: '16px', fontWeight: '900', color: '#000080' }}>{isRTL ? 'تقرير المسميات الوظيفية' : 'Job Titles Report'}</div>
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>ID: #{jobTitleForPrint.id}</div>
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                  <div style={{
                    marginTop: '12px',
                    border: '2px solid #000',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: 'bold',
                    backgroundColor: 'rgba(255, 255, 255, 0.3)'
                  }}>
                    {[
                      { label: t('jobTitles.pdfTemplate.name'), value: jobTitleForPrint.name || '—' },
                      { label: t('jobTitles.pdfTemplate.description'), value: jobTitleForPrint.description || '—' },
                      { label: t('jobTitles.pdfTemplate.createdAt'), value: formatDate(jobTitleForPrint.created_at) || '—' }
                    ].filter(row => row.value && row.value !== '—').map((row) => (
                      <div key={row.label} style={{ display: 'flex', borderBottom: '1px solid #000', minHeight: '38px' }}>
                        <div style={{
                          width: '30%',
                          borderLeft: '1px solid #000',
                          backgroundColor: 'rgba(227, 242, 253, 0.4)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          fontWeight: '700',
                          fontSize: '14px',
                          direction: 'rtl',
                          color: '#1976d2',
                          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                          fontWeight: 'bold',
                          padding: '0 10px'
                        }}>
                          {row.label}
                        </div>
                        <div style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          fontSize: '15px',
                          fontWeight: '500',
                          color: '#000',
                          direction: 'rtl',
                          padding: '0 10px',
                          backgroundColor: 'rgba(255, 255, 255, 0.3)',
                          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                          fontWeight: 'bold'
                        }}>
                          {row.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{
                  flexShrink: 0
                }}>
                  <img
                    src={`${publicUrl}/FooterAce.png`}
                    alt="Footer"
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain',
              borderTop: "4px double #000",
              paddingTop: "8px",
}}
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {pdfType === 'all' && pdfChunks && pdfChunks.length > 0 && (
        <>
          {pdfChunks.map((chunk, chunkIndex) => (
            <div
              key={chunkIndex}
              style={{
                position: 'absolute',
                left: '-9999px',
                top: 0
              }}
            >
              {renderPdfPage(chunk, chunkIndex, pdfChunks.length, `job-titles-pdf-all-${chunkIndex}`)}
            </div>
          ))}
        </>
      )}

      {/* Preview Modal - All */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && pdfChunks && pdfChunks.length > 0}
        title={t('jobTitles.title')}
        onClose={() => {
          setIsPreviewOpen(false);
          setPdfChunks([]);
        }}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
          {pdfChunks && pdfChunks.length > 0 && pdfChunks.map((chunk, chunkIdx) => (
            <div key={chunkIdx} style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
              {renderPdfPage(chunk, chunkIdx, pdfChunks.length)}
            </div>
          ))}
        </div>
      </PdfPreviewModal>

      {/* Single Job Title Preview Modal */}
      <PdfPreviewModal
        isOpen={isSinglePreviewOpen && singleJobTitleForPreview != null}
        title={t('jobTitles.title')}
        onClose={() => {
          setIsSinglePreviewOpen(false);
          setSingleJobTitleForPreview(null);
        }}
        onPrint={() => singleJobTitleForPreview && handlePrintJobTitle(singleJobTitleForPreview)}
        isGeneratingPdf={isGeneratingPdf}
      >
        {singleJobTitleForPreview && (
          <div style={{
            width: '794px',
            height: '1123px',
            backgroundColor: '#ffffff',
            color: '#000000',
            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: 'bold',
            padding: '40px',
            boxSizing: 'border-box',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
          }}>
            <div style={{ border: '5px solid #000', padding: '3px', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', minHeight: '1043px', height: 'auto', overflow: 'visible' }}>
              <div style={{
                border: '2px solid #000',
                minHeight: '1033px',
                height: '1033px',
                padding: '18px 18px 10px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: 'bold',
                direction: isRTL ? 'rtl' : 'ltr',
                backgroundColor: '#ffffff',
                boxSizing: 'border-box',
                width: '100%',
                overflow: 'visible'
              }}>
                <div style={{
                  position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: '95%', backgroundPosition: 'center center', backgroundRepeat: 'no-repeat',
                  opacity: 0.08, zIndex: 0, pointerEvents: 'none'
                }} />
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <img src={`${publicUrl}/HeaderAce.png`} alt="Header" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px', flexShrink: 0 }} crossOrigin="anonymous" />
                  <div style={{ textAlign: 'center', padding: '6px 0 8px', borderBottom: '1px solid #ddd', marginBottom: '4px', flexShrink: 0, direction: isRTL ? 'rtl' : 'ltr' }}>
                    <div style={{ fontSize: '16px', fontWeight: '900', color: '#000080' }}>{isRTL ? 'تقرير المسميات الوظيفية' : 'Job Titles Report'}</div>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>ID: #{singleJobTitleForPreview.id}</div>
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                    <div style={{ marginTop: '12px', border: '2px solid #000', borderRadius: '4px', overflow: 'hidden', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', fontWeight: 'bold', backgroundColor: 'rgba(255, 255, 255, 0.3)' }}>
                      {[
                        { label: t('jobTitles.pdfTemplate.name'), value: singleJobTitleForPreview.name || '—' },
                        { label: t('jobTitles.pdfTemplate.description'), value: singleJobTitleForPreview.description || '—' },
                        { label: t('jobTitles.pdfTemplate.createdAt'), value: formatDate(singleJobTitleForPreview.created_at) || '—' }
                      ].filter(row => row.value && row.value !== '—').map((row) => (
                        <div key={row.label} style={{ display: 'flex', borderBottom: '1px solid #000', minHeight: '38px' }}>
                          <div style={{ width: '30%', borderLeft: '1px solid #000', backgroundColor: 'rgba(227, 242, 253, 0.4)', display: 'flex', alignItems: 'center', padding: '0 10px', fontWeight: '700', fontSize: '14px', direction: 'rtl', color: '#1976d2', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
                            {row.label}
                          </div>
                          <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: '0 10px', fontSize: '15px', color: '#000', direction: 'rtl', backgroundColor: 'rgba(255, 255, 255, 0.3)', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', fontWeight: 'bold' }}>
                            {row.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <img src={`${publicUrl}/FooterAce.png`} alt="Footer" style={{ width: '100%', height: 'auto', objectFit: 'contain',
              borderTop: "4px double #000",
              paddingTop: "8px",
}} crossOrigin="anonymous" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </PdfPreviewModal>

      {/* Hidden Measurement Table */}
      <div
        id="job-titles-measurement-container"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          width: '714px', // Content area width inside PDF layout
          visibility: 'hidden',
          pointerEvents: 'none'
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '5%' }} /><col style={{ width: '32%' }} /><col style={{ width: '43%' }} /><col style={{ width: '20%' }} />
          </colgroup>
          <thead>
            <tr style={{ backgroundColor: 'rgba(238,242,247,0.7)' }}>
              <th style={colStyles.serial}>#</th>
              <th style={colStyles.name}>{t('jobTitles.table.name')}</th>
              <th style={colStyles.description}>{t('jobTitles.table.description')}</th>
              <th style={colStyles.created}>{t('jobTitles.table.createdAt')}</th>
            </tr>
          </thead>
          <tbody>
            {allJobTitlesForPdf.map((job, idx) => (
              <tr key={job.id}>
                <td style={colStyles.serial}>{idx + 1}</td>
                <td style={{ ...colStyles.name, direction: isRTL ? 'rtl' : 'ltr' }}>{job.name || '—'}</td>
                <td style={{ ...colStyles.description, direction: isRTL ? 'rtl' : 'ltr' }}>{job.description || '—'}</td>
                <td style={colStyles.created}>{job.created_at ? new Date(job.created_at).toLocaleDateString('en-US') : '--'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JobTitles;
